import { useEffect, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { toast } from 'sonner';
import { ChevronRight } from 'lucide-react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { timeAgo } from '../categoryMeta';
import ToolHeader, { NoAccess, Spinner } from './ToolHeader';

export default function StocktakeListPage() {
  const { tools } = useOutletContext();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState(null);
  const [categories, setCategories] = useState([]);
  const [picked, setPicked] = useState([]);
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    if (!tools?.stocktake) return;
    api.get('/stocktake/sessions/active').then((r) => setSessions(r.data.sessions || [])).catch(() => setSessions([]));
    api.get('/stocktake/categories').then((r) => setCategories(r.data.categories || [])).catch(() => {});
  }, [tools]);

  if (tools && !tools.stocktake) return <><ToolHeader title="Stock take" /><NoAccess /></>;

  const start = async () => {
    setStarting(true);
    try {
      const { data } = await api.post('/stocktake/sessions', { categories: picked.length ? picked : null, notes: 'Started from Staff Companion' });
      navigate(data.session_id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not start a stock take');
    } finally { setStarting(false); }
  };
  const toggle = (c) => setPicked(picked.includes(c) ? picked.filter((x) => x !== c) : [...picked, c]);

  return (
    <div data-testid="staff-app-stocktake-list">
      <ToolHeader title="Stock take" subtitle="Counts go to an admin for approval" />
      <div className="p-4 space-y-5">
        <section className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">In progress</p>
          {!sessions ? <Spinner /> : sessions.length === 0 ? (
            <p className="text-sm text-muted-foreground" data-testid="staff-app-stocktake-none">No active counts.</p>
          ) : sessions.map((s) => (
            <button key={s.session_id} onClick={() => navigate(s.session_id)} data-testid={`staff-app-stocktake-session-${s.session_id}`}
              className="staff-app-feed-item w-full flex items-center gap-3 rounded-xl border border-border bg-card px-3.5 py-3 text-left">
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-semibold truncate">{(s.filters?.categories || []).join(', ') || 'Whole store'}</span>
                <span className="block text-xs text-muted-foreground">{s.counted_products}/{s.total_products} counted · {s.status === 'pending_approval' ? 'Awaiting approval' : 'Counting'} · {timeAgo(s.created_at)}</span>
              </span>
              <ChevronRight className="w-4 h-4 text-muted-foreground" />
            </button>
          ))}
        </section>
        <section className="space-y-2 rounded-xl border border-dashed border-border p-3.5">
          <p className="text-sm font-semibold">Start a new count</p>
          <p className="text-xs text-muted-foreground">Pick categories to count, or leave empty for the whole store.</p>
          <div className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto">
            {categories.map((c) => (
              <button key={c} onClick={() => toggle(c)} data-testid={`staff-app-stocktake-cat-${c}`}
                className={`text-xs px-2.5 py-1.5 rounded-full border ${picked.includes(c) ? 'bg-primary text-primary-foreground border-primary' : 'border-border'}`}>{c}</button>
            ))}
          </div>
          <Button onClick={start} disabled={starting} className="w-full h-11" data-testid="staff-app-stocktake-start">
            {starting ? 'Starting…' : picked.length ? `Start count (${picked.length} categories)` : 'Start whole-store count'}
          </Button>
        </section>
      </div>
    </div>
  );
}
